/**
 * Inline-SVG paired bar chart. No chart library — keeps the bundle small
 * and the shared report fast to render even on a phone.
 */
type Series = {
  label: string;
  fillClass: string;
  values: number[];
};

export interface MiniBarChartProps {
  categories: string[];
  series: [Series, Series];
  height?: number;
  formatValue?: (v: number) => string;
  className?: string;
}

const DEFAULT_HEIGHT = 200;

export default function MiniBarChart({
  categories,
  series,
  height = DEFAULT_HEIGHT,
  formatValue = (v) => v.toLocaleString(),
  className,
}: MiniBarChartProps) {
  const allValues = [...series[0].values, ...series[1].values];
  const max = Math.max(1, ...allValues);

  const barWidth = 28;
  const groupGap = 40;
  const innerGap = 8;
  const leftPad = 8;
  const groupWidth = barWidth * 2 + innerGap;
  const totalWidth =
    leftPad + categories.length * groupWidth + (categories.length - 1) * groupGap;
  const chartHeight = height - 40;

  return (
    <div className={className}>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {series.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${s.fillClass}`} />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 overflow-x-auto">
        <svg
          width={totalWidth}
          height={height}
          role="img"
          aria-label={`${series[0].label} and ${series[1].label} across ${categories.join(", ")}`}
        >
          {categories.map((cat, i) => {
            const groupX = leftPad + i * (groupWidth + groupGap);
            return (
              <g key={cat}>
                {series.map((s, sIdx) => {
                  const value = s.values[i] ?? 0;
                  const barH = max === 0 ? 0 : (Math.max(0, value) / max) * chartHeight;
                  const x = groupX + sIdx * (barWidth + innerGap);
                  const y = chartHeight - barH;
                  return (
                    <g key={s.label}>
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={barH}
                        rx={3}
                        className={s.fillClass}
                      />
                      <text
                        x={x + barWidth / 2}
                        y={y - 4}
                        textAnchor="middle"
                        className="fill-foreground text-[10px] font-medium"
                      >
                        {value > 0 ? formatValue(value) : ""}
                      </text>
                    </g>
                  );
                })}
                <text
                  x={groupX + groupWidth / 2}
                  y={height - 8}
                  textAnchor="middle"
                  className="fill-muted-foreground text-xs"
                >
                  {cat}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
